<template>
  <div>
    <el-button
      :disabled="updatePasswordAlreadyRequested"
      @click="sendUpdatePasswordRequest"
      type="primary"
      size="small"
      :loading="passwordLoading"
    >
      Reset Password
    </el-button>
    <el-button
      @click="sendWelcomeEmail"
      type="primary"
      size="small"
      :loading="welcomeLoading"
    >
      Welcome aboard
    </el-button>
  </div>
</template>

<script>
import axios from 'axios';
import { AUTH_API_BASE, EMAIL_API_BASE } from '../env';
import { welcomeTemplate } from '../utils/emailTemplates';
import { getCustomerById } from '../api/customers';
import randomCode from '../utils/random';


export default {
  name: 'CustomerAccount',
  props: {
    id: { type: String, required: true },
    facility: { type: String, required: true },
  },
  watch: {
    async id() {
      this.loading = true;
      await this.getCustomer();
      await this.getCrmAccount();

      this.loading = false;
    },
  },
  computed: {
    updatePasswordAlreadyRequested() {
      return this.account.requiredActions.includes('UPDATE_PASSWORD');
    },
    verifyEmailAlreadyRequested() {
      return this.account.requiredActions.includes('VERIFY_EMAIL');
    },
  },
  async mounted() {
    this.loading = true;
    await this.getCustomer();
    await this.getCrmAccount();
    this.loading = false;
  },
  data() {
    return {
      emailLoading: false,
      passwordLoading: false,
      account: null,
      accountInfo: null,
      loading: true,
      welcomeLoading: false,
      customer: null,
      primaryContact: {
        password: '',
      },
    };
  },
  methods: {
    async getCustomer() {
      this.customer = await getCustomerById(this.id, this.facility, this.$keycloak.token);
    },
    async linkAccountWithCustomerId() {
      try {
        this.linkLoading = true;
        await axios.put(`${AUTH_API_BASE}/customer/${this.account.id}/attributes`, {
          attributes: {
            customerId: [this.customer._id],
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.linkLoading = false;
        await this.getCrmAccount();
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.response.data,
        });
      }
    },
    async getCrmAccount() {
      const response = await axios.get(`${AUTH_API_BASE}/customers`, {
        params: {
          email: this.customer.email,
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.account = response.data && response.data.length > 0 ? response.data[0] : null;
    },
    async sendUpdatePasswordRequest() {
      this.passwordLoading = true;
      try {
        const response = await axios.put(`${AUTH_API_BASE}/customers/${this.account.id}/execute-actions-email`,
          ['UPDATE_PASSWORD'], {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });

        this.$notify.success({
          title: 'Success',
          message: 'Success! Password reset email sent.',
        });
      } catch (err) {
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
      this.passwordLoading = false;
    },
    async sendWelcomeEmail() {
      this.welcomeLoading = true;
      await this.setPassword();
      try {
        const mjmlTemplate = welcomeTemplate(
          this.account.firstName, this.account.email, this.primaryContact.password,
        );
        await axios.post(`${EMAIL_API_BASE}/send/email`, {
          from: this.$keycloak.tokenParsed.email,
          to: this.customer.email,
          customer: this.id,
          facility: this.facility,
          subject: 'Welcome to ReadySpaces',
          mjml: true,
          body: mjmlTemplate,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.$message.success({
          title: 'Success',
          message: 'Email sent successfully',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: 'Send email failed',
        });
      }
      this.welcomeLoading = false;
    },
    async setPassword() {
      try {
        const code = randomCode();
        this.primaryContact.password = code;
        await axios.put(`${AUTH_API_BASE}/customer/${this.account.id}/reset-password`, {
          value: this.primaryContact.password,
          temporary: true,
          type: 'password',
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.response.data,
        });
      }
    },
    async sendVerifyEmailRequest() {
      this.emailLoading = true;
      try {
        const response = await axios.put(`${AUTH_API_BASE}/customers/${this.account.id}/execute-actions-email`,
          ['VERIFY_EMAIL', ...this.account.requiredActions], {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          });

        this.$notify.success({
          title: 'Success',
          message: response.data,
        });
      } catch (err) {
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
      this.emailLoading = false;
    },
    async enableCrm() {
      try {
        await axios.post(`${AUTH_API_BASE}/customers`, {
          email: this.customer.email,
          firstName: this.customer.primaryContact ? this.customer.primaryContact.firstName : 'Primary',
          lastName: this.customer.primaryContact ? this.customer.primaryContact.lastName : 'Contact',
          username: this.customer.email,
          emailVerified: true,
          enabled: true,
          attributes: {
            customerId: [this.customer._id],
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        await this.getCrmAccount();
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.response.data,
        });
      }
    },
    async sendEmail() {
      this.emailLoading = true;
      const body = `<mjml>
  <mj-body>
    <mj-section>
      <mj-column>

        <mj-image width="100px" src="https://email-attachments-public.s3.us-east-2.amazonaws.com/rs.png"></mj-image>

        <mj-divider border-color="#F45E43"></mj-divider>

        <mj-text font-size="20px" color="#009aca" font-family="helvetica">Dear ${this.customer.fullName},
        </mj-text>

                <mj-text font-size="15px" color="#009aca" font-family="helvetica">Please sign up on our secured CRM.
        </mj-text>
        <mj-button href="https://crm.api.readyspaces.com">
                  Sign Up
                  </mj-button>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;
      try {
        await axios.post(`${EMAIL_API_BASE}/send/email`, {
          from: this.$keycloak.tokenParsed.email,
          to: this.customer.email,
          customer: this.id,
          facility: this.facility,
          subject: 'Requested to create an account at Readyspaces platform.',
          mjml: true,
          body,
        });
        this.$emit('done');
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: 'Something went wrong',
        });
      }
      this.emailLoading = false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/CustomerAccount.vue